import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { map, take } from 'rxjs/operators';
import { parseISO } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { Revisione53DatiBusinessSelectors } from '../../../store/selectors/dati-business.selectors';
import { revisione53DatiSecondariSelectors } from '../../../store/selectors/dati-secondari.selectors';
import { MyElAderentiA, MyVerbale } from '../../../models/interfaces/revisione.interface';
import { getControl, getFormArray } from '@shared/helpers/form.helper';
import { withChangeDebounced } from '@shared/operators/with-change-debounced.operator';
import { isNilOrEmpty, toBoolean } from '@shared/helpers/utils.helper';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { cloneDeep } from 'lodash-es';
import { getDatiIdentificativiControl, getDatiIdentificativiGroup } from '../validators/helpers/dati-identificativi-form-validators.helper';

const DATA_TERMINE_WARNING_DESC = 'La data termine della società è trascorsa.';

export const setDatiIdentificativiSocietaRappresentanteListeners = (form: FormGroup, store: Store<Revisione53State>, toastrService: ToastrService): void => {
    my_CkScadenzaSocieta(form, store);
    my_DataTermine(form, store);
    my_CkAderenteA(form, store);
    my_SoggDispostoRev(form, store);
    my_RappresentanteCoopScelta(form, store);
    my_CkAttivEffettSvolta(form, store, toastrService);
    my_TipoSezEnte(form, store);
};

const my_CkScadenzaSocieta = (form: FormGroup, store: Store<Revisione53State>): void => {
    const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
    const my_CkScadenzaSocietaControl = getControl(datiIdentificativiGroup, 'my_CkScadenzaSocieta');
    my_CkScadenzaSocietaControl.valueChanges.pipe(withChangeDebounced(my_CkScadenzaSocietaControl)).subscribe((my_CkScadenzaSocieta) => {
        if (my_CkScadenzaSocieta === '1') {
            getControl(datiIdentificativiGroup, 'my_DataTermine').setValue('', { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_DataTermine = '';
                return myVerbaleClone;
            });
        }
    });
};

const my_DataTermine = (form: FormGroup, store: Store<Revisione53State>): void => {
    const my_DataTermineControl = getDatiIdentificativiControl(form, 'my_DataTermine');
    my_DataTermineControl.valueChanges.pipe(withChangeDebounced(my_DataTermineControl)).subscribe((my_DataTermine: string) => {
        const dataTermine = new Date(parseISO(my_DataTermine));
        const my_ElWarningFormArray = getFormArray(form, 'my_Warning.my_ElWarning');
        if (!isNilOrEmpty(my_DataTermine)) {
            const resultDifferenceInTime = new Date().getTime() - dataTermine.getTime();
            const resultDifferenceInDays = resultDifferenceInTime / (1000 * 3600 * 24);
            const visibValue = resultDifferenceInDays >= 0 ? '1' : '0';
            my_ElWarningFormArray.controls.find((group: FormGroup) => {
                if (getControl(group, 'my_DescWarning').value === DATA_TERMINE_WARNING_DESC) {
                    getControl(group, 'my_VisibWarning').patchValue(visibValue, { emitEvent: false });
                }
            });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                const warningEl = myVerbaleClone.my_Warning.my_ElWarning.find((el) => el.my_DescWarning === DATA_TERMINE_WARNING_DESC);
                if (warningEl) warningEl.my_VisibWarning = visibValue;
                return myVerbaleClone;
            });
        } else {
            my_ElWarningFormArray.controls.find((group: FormGroup) => {
                if (getControl(group, 'my_DescWarning').value === DATA_TERMINE_WARNING_DESC) {
                    getControl(group, 'my_VisibWarning').patchValue('0', { emitEvent: false });
                }
            });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                const warningEl = myVerbaleClone.my_Warning.my_ElWarning.find((el) => el.my_DescWarning === DATA_TERMINE_WARNING_DESC);
                if (warningEl) warningEl.my_VisibWarning = '0';
                return myVerbaleClone;
            });
        }
    });
};

const my_CkAderenteA = (form: FormGroup, store: Store<Revisione53State>): void => {
    const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
    const aderentiAFormArray = getFormArray(datiIdentificativiGroup, 'my_AderentiA.my_ElAderentiA');
    const my_TipoAdesione = getControl(datiIdentificativiGroup, 'my_TipoAdesione');
    aderentiAFormArray.valueChanges.pipe(withChangeDebounced(aderentiAFormArray)).subscribe((item: MyElAderentiA[]) => {
        let contatore = 0;
        item.forEach((element) => {
            if (toBoolean(element.my_SezVisibAderenteA.my_CkAderenteA) === true) {
                contatore++;
            }
        });

        const tipoAdesione = contatore > 1 ? 'PLURIMA' : 'UNICA';
        my_TipoAdesione.setValue(tipoAdesione, { emitEvent: false });
        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_TipoAdesione = tipoAdesione;
            return myVerbaleClone;
        });
    });
};

const my_SoggDispostoRev = (form: FormGroup, store: Store<Revisione53State>): void => {
    const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
    const my_SoggDispostoRevControl = getControl(datiIdentificativiGroup, 'my_SoggDispostoRev');
    my_SoggDispostoRevControl.valueChanges.pipe(withChangeDebounced(my_SoggDispostoRevControl)).subscribe((my_SoggDispostoRev: string) => {
        store
            .select(Revisione53DatiBusinessSelectors.getCentraleSpec(my_SoggDispostoRev))
            .pipe(take(1))
            .subscribe((soggDispost) => {
                const desc = soggDispost?.Nome ?? '';
                getControl(datiIdentificativiGroup, 'my_DescSoggDispostoRev')?.setValue(desc, { emitEvent: false });
                updateVerbaleOnStore(store, (myVerbale) => {
                    const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                    myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_DescSoggDispostoRev = desc;
                    return myVerbaleClone;
                });
            });
    });
};

const my_RappresentanteCoopScelta = (form: FormGroup, store: Store<Revisione53State>): void => {
    const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
    const my_RappresentanteCoopSceltaControl = getControl(datiIdentificativiGroup, 'my_RappresentanteCoopScelta');
    my_RappresentanteCoopSceltaControl.valueChanges.pipe(withChangeDebounced(my_RappresentanteCoopSceltaControl)).subscribe((my_RappresCoopScelta) => {
        if (!my_RappresentanteCoopSceltaControl.dirty) return; // ignora repopolazioni programmatiche (populateForm/refresh), reagisce solo alla scelta reale dell'utente

        if (my_RappresCoopScelta != '') {
            store
                .select(revisione53DatiSecondariSelectors.getAbLocaliRappresentanti)
                .pipe(
                    take(1),
                    map((localiRappresentanti) => localiRappresentanti?.find((legale) => legale.cod_fiscale === my_RappresCoopScelta))
                )
                .subscribe((rappresCoopScelta) => {
                    if (rappresCoopScelta) {
                        getControl(datiIdentificativiGroup, 'my_RappresentanteCoopNome')?.setValue(rappresCoopScelta.nome, { emitEvent: false });
                        getControl(datiIdentificativiGroup, 'my_RappresentanteCoopCognome')?.setValue(rappresCoopScelta.cognome, { emitEvent: false });
                        getControl(datiIdentificativiGroup, 'my_RappresentanteCoopCodFisc')?.setValue(rappresCoopScelta.cod_fiscaleModificabile, { emitEvent: false });
                        updateVerbaleOnStore(store, (myVerbale) => {
                            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                            const datiId = myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi;
                            datiId.my_RappresentanteCoopNome = rappresCoopScelta.nome;
                            datiId.my_RappresentanteCoopCognome = rappresCoopScelta.cognome;
                            datiId.my_RappresentanteCoopCodFisc = rappresCoopScelta.cod_fiscaleModificabile;
                            return myVerbaleClone;
                        });
                    }
                });
        } else {
            getControl(datiIdentificativiGroup, 'my_RappresentanteCoopNome')?.setValue('', { emitEvent: false });
            getControl(datiIdentificativiGroup, 'my_RappresentanteCoopCognome')?.setValue('', { emitEvent: false });
            getControl(datiIdentificativiGroup, 'my_RappresentanteCoopCodFisc')?.setValue('', { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                const datiId = myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi;
                datiId.my_RappresentanteCoopNome = '';
                datiId.my_RappresentanteCoopCognome = '';
                datiId.my_RappresentanteCoopCodFisc = '';
                return myVerbaleClone;
            });
        }
    });
};

const my_CkAttivEffettSvolta = (form: FormGroup, store: Store<Revisione53State>, toastrService: ToastrService): void => {
    const ckAttivControl = getDatiIdentificativiControl(form, 'my_CkAttivEffettSvolta');
    ckAttivControl.valueChanges.pipe(withChangeDebounced(ckAttivControl)).subscribe((ckValue) => {
        const visibValue = ckValue === '0' ? '1' : '0';
        const my_ElWarningFormArray = getFormArray(form, 'my_Warning.my_ElWarning');

        if (my_ElWarningFormArray.controls?.length) {
            getControl(my_ElWarningFormArray.controls[0] as FormGroup, 'my_VisibWarning')?.setValue(visibValue, { emitEvent: false });
        }

        if (ckValue === '0') {
            toastrService.warning("L'attività effettivamente svolta non è coerente con l'oggetto sociale. Irrogare la diffida", null, { timeOut: 1500 });
        }

        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_CkAttivEffettSvolta = ckValue as string;
            if (myVerbaleClone.my_Warning.my_ElWarning?.[0]) {
                myVerbaleClone.my_Warning.my_ElWarning[0].my_VisibWarning = visibValue;
            }
            return myVerbaleClone;
        });
    });
};

const my_TipoSezEnte = (form: FormGroup, store: Store<Revisione53State>): void => {
    const tipoSezEnteControl = getDatiIdentificativiControl(form, 'my_TipoSezEnte');
    const ckPeriodicReviControl = getDatiIdentificativiControl(form, 'my_CkPeriodicRevi');

    tipoSezEnteControl.valueChanges.pipe(withChangeDebounced(tipoSezEnteControl)).subscribe((tipoSezEnte: string) => {
        if (tipoSezEnte === '2') {
            ckPeriodicReviControl.patchValue('1', { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_CkPeriodicRevi = '1';
                return myVerbaleClone;
            });
        }
    });
};
